import {
  AIRecommendation,
  AIPerformanceReport,
  AIAssessment,
  AIStudyPlan,
  AIContentGeneration,
  AIFeedback
} from '../types';
import { GoogleGenerativeAI } from "@google/generative-ai";

// AI Service - Integrates with Gemini AI API
export class AIService {
  private static instance: AIService;

  private geminiApiKey: string | undefined;
  private genAI: GoogleGenerativeAI | undefined;
  private model: any;

  private static readonly SYSTEM_CONTEXT = `
      Identity & Background:
      - You are Profstudymate, the intelligent learning assistant for Profs Training Solutions.
      - Profs Training Solutions is a leading ICAG and CITG E-learning tuition center in Ghana with over a decade of expertise.
      - Key Contacts: +233243443934 / +233244990773.
      - Primary Mission: Helping Ghanaian students pass ICAG (Chartered Accountants Ghana) and CITG (Taxation Ghana) exams on their first attempt.

      Core Offerings:
      - Digital summary notes, lecture videos, books, articles, and exam practice resources.
      - Mobile-first learning system replacing traditional physical classes and WhatsApp groups.
      - Features: Video learning (pre-recorded), Practice & Exam Prep (quizzes/tests), Live Classes (Zoom integration), Progress Dashboard, and Built-in Bookshop.

      Hall of Excellence (Top Achievers):
      - Milicent Naa (Overall Best Final 2 Student)
      - David Awudza (Strategic Tax Planning - Overall Best Final 2)
      - Musah Crispinus (Advanced Taxation Practice - Overall Best Final 2)
      - Owureku Nsiah (Overall Best Final 2)
      - Michael Ansah (Oil and Gas Taxation - Overall Best Final 1)
      - Specialized Awards: Felicia Omotoyo (Public Sector), Emmanuel Ampadu (Accounting & Finance), Beryl Bonney (International Taxation).

      Platform Nature:
      - This is primarily a mobile-first learning app and e-learning platform, not just a standard website.
      - Education content comes from the tuition center (Profs Training Solutions) and tech/app is built by Redizeuz (Oteng Emmanuel).

      Instructions for your persona:
      - Always be encouraging, professional, and knowledgeable about ICAG/CITG.
      - Mention Profstudymate features if students ask for study help.
      - Refer students to support contacts for technical issues or detailed enrollment queries.
      - Ensure answers are localized for the Ghanaian educational context.
  `;

  private constructor() {
    this.geminiApiKey = process.env.REACT_APP_GEMINI_API_KEY;

    // Initialize Gemini if key is available
    if (this.geminiApiKey) {
      try {
        this.genAI = new GoogleGenerativeAI(this.geminiApiKey);
        // Using gemini-1.5-flash as default for responsiveness
        this.model = this.genAI.getGenerativeModel({ model: "gemini-flash-latest" });
        console.log('✅ Profstudymate AI client initialized');
      } catch (error) {
        console.error('❌ Failed to initialize Profstudymate AI:', error);
      }
    } else {
      console.warn('⚠️ REACT_APP_GEMINI_API_KEY not found. AI features will be unavailable.');
    }
  }

  static getInstance(): AIService {
    if (!AIService.instance) {
      AIService.instance = new AIService();
    }
    return AIService.instance;
  }



  // Check AI service status
  public getAIStatus(): { isAvailable: boolean; primaryService: string; fallbackService: string | null } {
    const hasGemini = !!this.geminiApiKey && !!this.model;

    return {
      isAvailable: hasGemini,
      primaryService: hasGemini ? 'Profstudymate AI' : 'None',
      fallbackService: null
    };
  }

  // Test AI service connectivity
  public async testAIConnectivity(): Promise<boolean> {
    if (!this.geminiApiKey || !this.model) return false;

    try {
      const testPrompt = 'Hello, this is a connectivity test. Respond with "Ready".';
      const result = await this.callAIAPI(testPrompt);
      console.log('✅ AI service connectivity test passed:', result);
      return true;
    } catch (error) {
      console.error('❌ AI service connectivity test failed:', error);
      return false;
    }
  }

  // Helper method for robust JSON parsing from AI responses
  private parseAIResponse(aiResponse: string, expectedKey?: string): any {
    try {
      // Try multiple parsing strategies

      // Strategy 1: Find the largest valid JSON object
      let bestJson = null;
      let bestLength = 0;

      // Look for JSON objects with different starting positions
      for (let start = 0; start < aiResponse.length; start++) {
        const jsonStart = aiResponse.indexOf('{', start);
        if (jsonStart === -1) break;

        // Find the matching closing brace by counting braces
        let braceCount = 0;
        let jsonEnd = -1;

        for (let i = jsonStart; i < aiResponse.length; i++) {
          if (aiResponse[i] === '{') braceCount++;
          if (aiResponse[i] === '}') {
            braceCount--;
            if (braceCount === 0) {
              jsonEnd = i;
              break;
            }
          }
        }

        if (jsonEnd !== -1) {
          try {
            const jsonString = aiResponse.substring(jsonStart, jsonEnd + 1);
            const parsed = JSON.parse(jsonString);

            // Validate the parsed response
            if (expectedKey) {
              if (parsed[expectedKey] && jsonString.length > bestLength) {
                bestJson = parsed;
                bestLength = jsonString.length;
              }
            } else if (jsonString.length > bestLength) {
              bestJson = parsed;
              bestLength = jsonString.length;
            }
          } catch (e) {
            // Continue to next potential JSON
            continue;
          }
        }
      }

      if (bestJson) {
        console.log(`✅ AI response parsed successfully (found ${bestLength} character JSON)`);
        return bestJson;
      }

      // Strategy 2: Try to extract specific content using regex
      if (expectedKey) {
        console.log(`⚠️ JSON parsing failed, attempting regex extraction for ${expectedKey}`);

        if (expectedKey === 'questions' && aiResponse.includes('"questions"')) {
          // Try to extract questions array
          const questionsMatch = aiResponse.match(/"questions"\s*:\s*\[([^\]]+)\]/);
          if (questionsMatch) {
            console.log('✅ Extracted questions using regex fallback');
            return { questions: [] }; // Return empty questions as fallback
          }
        }

        if (expectedKey === 'summary' && aiResponse.includes('"summary"')) {
          // Try to extract summary
          const summaryMatch = aiResponse.match(/"summary"\s*:\s*"([^"]+)"/);
          if (summaryMatch) {
            console.log('✅ Extracted summary using regex fallback');
            return { summary: summaryMatch[1], keyPoints: [], importantTerms: [] };
          }
        }
      }

      // Strategy 3: Look for any valid JSON structure
      console.log('⚠️ Attempting to find any valid JSON structure');
      const jsonMatches = aiResponse.match(/\{[^{}]*\}/g);
      if (jsonMatches) {
        for (const match of jsonMatches) {
          try {
            const parsed = JSON.parse(match);
            if (expectedKey ? parsed[expectedKey] : true) {
              console.log('✅ Found valid JSON structure using fallback method');
              return parsed;
            }
          } catch (e) {
            continue;
          }
        }
      }

      console.warn('All parsing strategies failed, returning null');
      return null;
    } catch (parseError) {
      console.warn('JSON parsing error:', parseError);
      return null;
    }
  }

  // AI API Methods
  private async callGeminiAPI(prompt: string, signal?: AbortSignal): Promise<string> {
    try {
      if (!this.geminiApiKey || !this.model) {
        throw new Error('Gemini API not properly configured');
      }

      console.log('🔄 Calling Profstudymate AI...');

      const result = await this.model.generateContent(prompt);
      const response = await result.response;
      const text = response.text();

      return text || '';
    } catch (error: any) {
      console.error('Profstudymate AI error:', error);
      throw error;
    }
  }

  private async callAIAPI(prompt: string, retryCount: number = 0, signal?: AbortSignal): Promise<string> {
    const maxRetries = 2;

    try {
      if (!this.geminiApiKey || !this.model) {
        throw new Error('Gemini API not properly configured');
      }

      console.log(`🔄 Attempting Profstudymate AI call (attempt ${retryCount + 1})`);
      return await this.callGeminiAPI(prompt, signal);
    } catch (error: any) {
      console.warn(`Profstudymate AI failed (attempt ${retryCount + 1}):`, error.message);
      if (retryCount < maxRetries) {
        console.log(`🔄 Retrying Profstudymate AI... (${retryCount + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
        return this.callAIAPI(prompt, retryCount + 1, signal);
      }
      throw error;
    }
  }

  // AI-Based Personalized Learning Assistance
  async generateRecommendations(userId: string, userPerformance: any): Promise<AIRecommendation[]> {
    try {
      const prompt = `Based on this student performance data, generate 3 personalized learning recommendations.

      Student Performance: ${JSON.stringify(userPerformance)}

      Respond with a JSON object in this exact format:
      {
        "recommendations": [
          {
            "title": "Course or activity name",
            "description": "Detailed description of the recommendation",
            "reason": "Why this is recommended based on performance",
            "type": "course|practice|resource|activity",
            "confidence": 0.8
          }
        ]
      }

      Rules:
      - Always respond with valid JSON object containing a "recommendations" array
      - Include exactly 3 recommendations
      - Make recommendations practical and actionable
      - Base confidence on how well the recommendation fits the student's needs
      - Focus on areas where the student can improve`;

      const aiResponse = await this.callAIAPI(prompt);

      // Use the robust parsing helper
      const parsedResponse = this.parseAIResponse(aiResponse);

      if (parsedResponse && Array.isArray(parsedResponse) && parsedResponse.length > 0) {
        return parsedResponse.map((rec, index) => ({
          id: `rec_${Date.now()}_${index}`,
          userId,
          ...rec,
          createdAt: new Date(),
          isRead: false
        }));
      } else if (parsedResponse && typeof parsedResponse === 'object' && parsedResponse.recommendations) {
        return parsedResponse.recommendations.map((rec: any, index: number) => ({
          id: `rec_${Date.now()}_${index}`,
          userId,
          ...rec,
          createdAt: new Date(),
          isRead: false
        }));
      }

      throw new Error('Failed to generate valid recommendations');
    } catch (error) {
      console.error('❌ AI API completely failed:', error);
      throw new Error('AI service temporarily unavailable. Please try again.');
    }
  }

  // AI-Driven Performance Tracking & Feedback
  async generatePerformanceReport(
    userId: string,
    courseId: string,
    period: 'weekly' | 'monthly' | 'quarterly'
  ): Promise<AIPerformanceReport> {
    try {
      const now = new Date();
      const startDate = new Date();

      switch (period) {
        case 'weekly': startDate.setDate(now.getDate() - 7); break;
        case 'monthly': startDate.setMonth(now.getMonth() - 1); break;
        case 'quarterly': startDate.setMonth(now.getMonth() - 3); break;
      }

      const prompt = `Analyze this student's performance for the period ${period} starting from ${startDate.toISOString()}.
      Course ID: ${courseId}
      
      Generate a detailed performance report in JSON format:
      {
        "overallScore": 85,
        "strengths": ["string"],
        "weaknesses": ["string"],
        "improvementAreas": ["string"],
        "suggestedResources": ["string"],
        "learningGaps": ["string"],
        "nextSteps": ["string"]
      }`;

      const aiResponse = await this.callAIAPI(prompt);
      const parsed = this.parseAIResponse(aiResponse);

      return {
        id: `report_${Date.now()}`,
        userId,
        courseId,
        period,
        startDate,
        endDate: now,
        overallScore: parsed?.overallScore || 0,
        strengths: parsed?.strengths || [],
        weaknesses: parsed?.weaknesses || [],
        improvementAreas: parsed?.improvementAreas || [],
        suggestedResources: parsed?.suggestedResources || [],
        learningGaps: parsed?.learningGaps || [],
        nextSteps: parsed?.nextSteps || [],
        createdAt: now
      };
    } catch (error) {
      console.error('Failed to generate performance report:', error);
      throw error;
    }
  }

  async gradeAssessment(assessment: AIAssessment, studentAnswer: any): Promise<AIFeedback> {
    try {
      const prompt = `Grade this student assessment.
      Assessment: ${JSON.stringify(assessment)}
      Student Answer: ${JSON.stringify(studentAnswer)}
      
      Respond in JSON:
      {
        "score": 0-100,
        "feedback": "Detailed feedback",
        "improvementSuggestions": ["string"],
        "recommendedResources": ["string"],
        "nextSteps": ["string"]
      }`;

      const aiResponse = await this.callAIAPI(prompt);
      const parsed = this.parseAIResponse(aiResponse);

      return {
        id: `feedback_${Date.now()}`,
        userId: '',
        assessmentId: assessment.id,
        score: parsed?.score || 0,
        feedback: parsed?.feedback || 'No feedback provided',
        improvementSuggestions: parsed?.improvementSuggestions || [],
        recommendedResources: parsed?.recommendedResources || [],
        nextSteps: parsed?.nextSteps || [],
        createdAt: new Date()
      };
    } catch (error) {
      console.error('Failed to grade assessment:', error);
      throw error;
    }
  }

  // AI-Powered Content Generation & Customization
  async generateContent(
    courseId: string,
    lessonId: string,
    type: 'quiz' | 'summary' | 'flashcards' | 'practice_exercises' | 'likely_questions',
    lessonContent: string,
    contentLength: number = 5,
    signal?: AbortSignal
  ): Promise<AIContentGeneration> {
    try {
      const prompt = this.getDetailedContentPrompt(type as any, lessonContent, contentLength);

      console.log(`🔍 Generating ${type} content with AI...`);
      const aiResponse = await this.callAIAPI(prompt, 0, signal);

      const parsedResponse = this.parseAIResponse(aiResponse);

      if (parsedResponse && Object.keys(parsedResponse).length > 0) {
        return {
          id: `content_${Date.now()}`,
          courseId,
          lessonId,
          type,
          content: parsedResponse,
          metadata: {
            difficulty: 'medium',
            estimatedTime: 15,
            tags: ['AI-generated', 'practice'],
            aiModel: 'gemini-flash-latest'
          },
          createdAt: new Date(),
          isActive: true
        };
      }

      throw new Error(`Failed to generate ${type} content`);
    } catch (error) {
      console.error(`❌ AI content generation for ${type} failed:`, error);
      throw error;
    }
  }

  private getContentPrompt(type: string): string {
    switch (type) {
      case 'quiz':
        return 'Create 3 multiple choice questions with 4 options each and correct answers. Include explanations.';
      case 'summary':
        return 'Extract key points, create a summary, and list important terms.';
      case 'flashcards':
        return 'Create 5 question-answer pairs for memorization.';
      case 'practice_exercises':
        return 'Create 3 practical exercises with descriptions and problem types.';
      default:
        return 'Generate relevant content.';
    }
  }

  private getDetailedContentPrompt(type: 'quiz' | 'summary' | 'flashcards' | 'practice_exercises' | 'likely_questions', lessonContent: string, contentLength: number = 5): string {
    const basePrompt = `Based on this lesson content, generate ${type} content.

Lesson Content: "${lessonContent}"

`;

    switch (type) {
      case 'quiz':
        return basePrompt + `Respond with a JSON object in this exact format:
{
  "questions": [
    {
      "question": "Question text here?",
      "options": ["Option A", "Option B", "Option C", "Option D"],
      "correctAnswer": 0,
      "explanation": "Brief explanation of why this is correct"
    }
  ]
}

Rules:
- Always respond with valid JSON
- Include exactly ${contentLength} questions
- correctAnswer should be the index (0-3) of the correct option
- Make questions relevant to the lesson content
- Include clear explanations for each answer`;

      case 'summary':
        return basePrompt + `Respond with a JSON object in this exact format:
{
  "summary": "A comprehensive summary paragraph of the lesson content",
  "keyPoints": ["Key point 1", "Key point 2", "Key point 3", "Key point 4"],
  "importantTerms": ["Term 1", "Term 2", "Term 3"]
}

Rules:
- Always respond with valid JSON
- Summary should be clear and concise
- Include 4 key points that highlight main concepts
- List 3 important terms from the lesson`;

      case 'flashcards':
        return basePrompt + `Respond with a JSON object in this exact format:
{
  "cards": [
    {
      "front": "Question or concept on the front",
      "back": "Answer or explanation on the back"
    }
  ]
}

Rules:
- Always respond with valid JSON
- Include exactly ${contentLength} flashcards
- Make cards educational and relevant to the lesson
- Front should be a question or concept
- Back should be a clear answer or explanation`;

      case 'practice_exercises':
        return basePrompt + `Respond with a JSON object in this exact format:
{
  "exercises": [
    {
      "type": "problem_solving",
      "title": "Exercise Title",
      "description": "Detailed description of the exercise",
      "instructions": "Step-by-step instructions for the student",
      "difficulty": "beginner|intermediate|advanced"
    }
  ]
}

Rules:
- Always respond with valid JSON
- Include exactly ${contentLength} exercises
- Make exercises practical and relevant to the lesson
- Vary difficulty levels appropriately
- Provide clear instructions for each exercise`;

      default:
        return basePrompt + 'Generate relevant content in JSON format.';
      case 'likely_questions':
        return basePrompt + `Strictly generate short-answer or MCQ style 'likely exam questions' derived ONLY from the provided content. Do not invent topics.
Respond with a JSON object in this exact format:
{
  "questions": [
    {
      "question": "Question text here?",
      "options": ["Option A", "Option B", "Option C", "Option D"],
      "correctAnswer": 0,
      "explanation": "One-sentence rationale grounded in the provided content"
    }
  ]
}

Rules:
- Always respond with valid JSON
- Include exactly ${contentLength} questions
- Every question must be directly supported by the provided content
- Use clear, exam-ready phrasing
- If the content lacks enough material, return an empty questions array`;
    }
  }

  async generateStudyPlan(
    userId: string,
    courseId: string,
    userSchedule: any,
    learningGoals: string[]
  ): Promise<AIStudyPlan> {
    try {
      const prompt = `Generate a personalized study plan for user ${userId} taking course ${courseId}.
      Schedule: ${JSON.stringify(userSchedule)}
      Goals: ${learningGoals.join(', ')}
      
      Respond in JSON:
      {
        "title": "Study Plan Title",
        "description": "Plan description",
        "dailySchedule": [
          {
            "dayOfWeek": 0-6,
            "startTime": "HH:MM",
            "duration": minutes,
            "activities": ["string"],
            "materials": ["string"]
          }
        ],
        "estimatedDuration": weeks,
        "difficulty": "beginner|intermediate|advanced"
      }`;

      const aiResponse = await this.callAIAPI(prompt);
      const parsed = this.parseAIResponse(aiResponse);

      return {
        id: `plan_${Date.now()}`,
        userId,
        courseId,
        title: parsed?.title || 'Personalized Study Plan',
        description: parsed?.description || 'Study plan based on your goals',
        dailySchedule: parsed?.dailySchedule || [],
        weeklyGoals: learningGoals,
        estimatedDuration: parsed?.estimatedDuration || 10,
        difficulty: parsed?.difficulty || 'intermediate',
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('Failed to generate study plan:', error);
      throw error;
    }
  }

  // AI-Powered Chatbot Integration
  async processChatMessage(
    userId: string,
    message: string,
    context: any
  ): Promise<{ response: string; confidence: number; escalated: boolean }> {
    try {
      const prompt = `${AIService.SYSTEM_CONTEXT}
      A student asks: "${message}"

      Context of conversation: ${JSON.stringify(context)}

      Respond in this exact JSON format:
      {
        "response": "Your helpful answer here",
        "confidence": 0.8,
        "escalated": false
      }

      Rules:
      - Always respond with valid JSON.
      - Keep responses helpful, educational, and brand-consistent.
      - If you can't help with a specific administrative issue, set escalated to true and suggest contacting support.`;

      const aiResponse = await this.callAIAPI(prompt);

      try {
        // Try to extract JSON from the response (AI might add extra text)
        let jsonStart = aiResponse.indexOf('{');
        let jsonEnd = aiResponse.lastIndexOf('}');

        if (jsonStart !== -1 && jsonEnd !== -1) {
          const jsonString = aiResponse.substring(jsonStart, jsonEnd + 1);
          const parsedResponse = JSON.parse(jsonString);

          if (parsedResponse.response && typeof parsedResponse.confidence === 'number') {
            console.log('✅ AI chatbot response parsed successfully');
            return {
              response: parsedResponse.response,
              confidence: Math.max(0, Math.min(1, parsedResponse.confidence)), // Ensure confidence is 0-1
              escalated: Boolean(parsedResponse.escalated)
            };
          }
        }

        // If JSON parsing fails, try to extract just the response text
        if (aiResponse.includes('"response"')) {
          const responseMatch = aiResponse.match(/"response"\s*:\s*"([^"]+)"/);
          if (responseMatch) {
            return {
              response: responseMatch[1],
              confidence: 0.7,
              escalated: false
            };
          }
        }

        return {
          response: aiResponse.trim() || 'I processed your request but the formatting was unexpected.',
          confidence: 0.6,
          escalated: false
        };

      } catch (parseError) {
        return {
          response: aiResponse.trim() || 'Connection issue occurred.',
          confidence: 0.6,
          escalated: false
        };
      }
    } catch (error: any) {
      console.error('AI chatbot completely failed:', error);
      return {
        response: `Profstudymate encountered an error: ${error.message || 'Connection failed'}. Please try again.`,
        confidence: 0,
        escalated: true
      };
    }
  }

}

export const aiService = AIService.getInstance();
